import InfoIcon from '../InfoIcon';
import { UseFormRegister } from 'react-hook-form';

interface TextInputProps<T extends object> {
  inputKey: keyof T;
  label?: string;
  isTextArea: boolean;
  register: UseFormRegister<T>;
  infoText?: string;
  placeholder: string;
  inputType?: string;
  width?: string;
  height?: string;
}

interface TextAreaProps<T extends object> {
  inputKey: keyof T;
  label?: string;
  register: UseFormRegister<any>;
  infoText?: string;
  placeholder: string;
}

interface InputProps<T extends object> {
  inputKey: keyof T;
  label?: string;
  register: UseFormRegister<any>;
  infoText?: string;
  placeholder: string;
  inputType?: string;
  width?: string;
  height?: string;
}

/**
 * Text input component
 * @param param0  - inputKey: key of the input in the widgetState
 *              - labelText: label text
 *           - isTextArea: boolean to determine if the input is a text area or not
 *        - updateWidgetStateTitle: function to update the widgetState
 *     - widgetState: state of the widget
 *  - infoText: text to display when hovering over the info icon
 * @returns - text input component
 */
export const ControlledTextInputContainer = <T extends object>({ inputKey, label, isTextArea, register, infoText, placeholder, inputType, width = '66.666667%', height = '2rem' }: TextInputProps<T>) => {
  return (
    <div className='flex gap-2 mt-2 w-full'>
      {label && <ControlledTextInputLabel labelText={label} infoText={infoText} />}

      {isTextArea && <ControlledTextAreaInput<T> register={register} inputKey={inputKey} label={label} placeholder={placeholder} />}
      {!isTextArea && <ControlledTextInput<T> register={register} inputKey={inputKey} label={label} placeholder={placeholder} inputType={inputType} width={width} height={height} />}
    </div>
  );
};

const ControlledTextInputLabel = ({ labelText, infoText }: { labelText: string; infoText?: string }) => {
  return (
    <label htmlFor='titleInput' className='text-base font-normal text-[#858796] mr-10 flex w-1/3'>
      {labelText}
      {infoText && <InfoIcon className='ml-2' infoText={infoText} />}
    </label>
  );
};

/**
 * Text input with label
 * @param param0  - inputKey: key of the input in the widgetState
 *               - labelText: label text
 *              - updateWidgetStateTitle: function to update the widgetState
 *             - widgetState: state of the widget
 *
 * @returns  - text input with label
 */
export const ControlledTextAreaInput = <T extends object>({ inputKey, placeholder, register }: TextAreaProps<T>) => {
  return <textarea {...register(inputKey.toString())} name={inputKey as string} placeholder={placeholder} className='w-2/3 min-h-[220px] border border-[#e3e6f0] rounded p-2' />;
};

/**
 * Input with label
 * @param param0 - inputKey: key of the input in the widgetState
 *             - labelText: label text
 *           - updateWidgetStateTitle: function to update the widgetState
 *         - widgetState: state of the widget
 *
 * @returns
 */
export const ControlledTextInput = <T extends object>({ inputKey, placeholder, register, inputType, width, height }: InputProps<T>) => {
  return (
    <input
      {...register(inputKey.toString())}
      type={inputType ?? 'text'}
      placeholder={placeholder}
      style={{
        width: width,
        height: height,
        border: '1px solid #e3e6f0',
        borderRadius: '0.25rem',
        padding: '0.5rem',
      }}
    />
  );
};
